/**
 * TestOps Command Service - API client for Athena assistant
 * Handles integration status, action items, and chat functionality
 */

import axios from 'axios'
import { Integration, ActionItem, ActivePin, ActivePinKind, ToolCallRecord } from './external-types'
import {
  createMCPConnection,
  deleteMCPConnection,
  fetchMCPConnections,
  fetchMCPServers,
  testMCPConnection,
} from './mcp'
import { tokenStorage } from '@ytest/auth-client'

const API_URL = import.meta.env.VITE_ATHENA_API_URL || 'http://localhost:8002'
const IDENTITY_API_URL = import.meta.env.VITE_IDENTITY_API_URL || 'https://api.ytest.ai'
const MAX_CHAT_HISTORY_MESSAGES = 40

/**
 * Try to silently refresh the access token.
 * Returns true if refresh succeeded and new tokens are stored.
 */
async function tryRefreshToken(): Promise<boolean> {
  // No stored refresh token (signed in through the SSO cookie): send none,
  // and identity-api uses the httpOnly refresh_token cookie instead.
  const refreshToken = tokenStorage.getRefreshToken()
  try {
    const res = await fetch(`${IDENTITY_API_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(refreshToken ? { refresh_token: refreshToken } : {}),
      credentials: 'include',
    })
    if (!res.ok) return false
    const data = await res.json()
    tokenStorage.setTokens(data.access_token, data.refresh_token)
    localStorage.setItem('token', data.access_token)
    return true
  } catch {
    return false
  }
}

/**
 * Client-safe projection of the server's pending-action envelope.
 * `nonce` is the single-shot authorization token that must be echoed
 * back on the next chat request to authorise the proposed destructive
 * call. See app/services/testops_hub_consent.py on the backend for the
 * full envelope shape — this is the subset that ever crosses the wire.
 */
export interface PendingActionPayload {
  nonce: string
  provider: string
  status: string
  tool_name: string
  connection_id: string
  arguments: Record<string, unknown>
  summary?: string
  display?: {
    provider_label?: string
    title?: string
    details?: string[]
    href?: string | null
    confirm_label?: string
    cancel_label?: string
    summary?: string
  }
}

// Create axios instance with auth
const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  }
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// On 401, attempt silent refresh then retry once
api.interceptors.response.use(undefined, async (error) => {
  const original = error.config
  if (error.response?.status === 401 && !original._retried) {
    original._retried = true
    const refreshed = await tryRefreshToken()
    if (refreshed) {
      original.headers.Authorization = `Bearer ${localStorage.getItem('access_token')}`
      return api(original)
    }
  }
  return Promise.reject(error)
})

/**
 * Integration Management
 */
export const testOpsService = {
  async chatStream(
    message: string,
    history: Array<{ role: 'user' | 'assistant'; content: string }>,
    conversationId?: string | null,
    portfolioId?: string | null,
    approvalNonce?: string | null,
    file?: File,
    callbacks: {
      onStatus?: (message: string) => void
      onToolCall?: (data: {
        call_id: string
        tool: string
        provider: string
        args: Record<string, unknown>
        status: 'running'
        started_at: string
      }) => void
      onToolResult?: (data: ToolCallRecord) => void
      onContent?: (content: string) => void
      onContentReplace?: (content: string) => void
      onPendingAction?: (action: PendingActionPayload) => void
      onPanelUpdate?: (data: { action: string; resourceKey: string; resourceType: string; title: string; provider: string; content: unknown }) => void
      onDone?: (data: { conversation_id: string; model: string; sources: any[]; pending_action: any; panel_state: any }) => void
      onError?: (message: string) => void
    } = {},
    signal?: AbortSignal,
  ) {
    const token = localStorage.getItem('access_token')
    const normalizedHistory = history
      .filter(m => m.content.trim())
      .slice(-MAX_CHAT_HISTORY_MESSAGES)

    // Use multipart/form-data when a file is attached, otherwise JSON
    let body: BodyInit
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
    }

    if (file) {
      const formData = new FormData()
      formData.append('message', message.trim())
      if (conversationId) formData.append('conversation_id', conversationId)
      formData.append('history', JSON.stringify(normalizedHistory))
      if (portfolioId) formData.append('portfolio_id', portfolioId)
      if (approvalNonce) formData.append('approval_nonce', approvalNonce)
      formData.append('file', file)
      body = formData
      // Let browser set Content-Type with boundary
    } else {
      headers['Content-Type'] = 'application/json'
      body = JSON.stringify({
        message: message.trim(),
        conversation_id: conversationId ?? null,
        history: normalizedHistory,
        portfolio_id: portfolioId ?? null,
        approval_nonce: approvalNonce ?? null,
      })
    }

    const url = file
      ? `${API_URL}/api/v1/testops-hub/chat/stream-with-file`
      : `${API_URL}/api/v1/testops-hub/chat/stream`

    let response = await fetch(url, {
      method: 'POST',
      headers,
      body,
      signal,
    })

    // On 401, try silent refresh and retry once
    if (response.status === 401) {
      const refreshed = await tryRefreshToken()
      if (refreshed) {
        const newToken = localStorage.getItem('access_token')
        headers['Authorization'] = `Bearer ${newToken}`
        response = await fetch(url, {
          method: 'POST',
          headers,
          body,
          signal,
        })
      }
    }

    if (!response.ok) {
      if (response.status === 422) {
        const body = await response.json().catch(() => null)
        const isHistoryTooLong = JSON.stringify(body ?? '').includes('string_too_long')
        if (isHistoryTooLong) {
          callbacks.onError?.('__CONVERSATION_TOO_LONG__')
          return
        }
      }
      const errorText = await response.text()
      callbacks.onError?.(errorText || 'Stream request failed')
      return
    }

    const reader = response.body?.getReader()
    if (!reader) {
      callbacks.onError?.('No response body')
      return
    }

    const decoder = new TextDecoder()
    let buffer = ''
    let currentEvent = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim()
        } else if (line.startsWith('data: ') && currentEvent) {
          try {
            const data = JSON.parse(line.slice(6))
            switch (currentEvent) {
              case 'status': callbacks.onStatus?.(data.message); break
              case 'tool_call': callbacks.onToolCall?.(data); break
              case 'tool_result': callbacks.onToolResult?.(data); break
              case 'content': callbacks.onContent?.(data.content); break
              case 'content_replace': callbacks.onContentReplace?.(data.content); break
              case 'pending_action': callbacks.onPendingAction?.(data); break
              case 'panel_update': callbacks.onPanelUpdate?.(data); break
              case 'done': callbacks.onDone?.(data); break
              case 'error': callbacks.onError?.(data.message); break
            }
          } catch { /* ignore parse errors */ }
          currentEvent = ''
        }
      }
    }
  },

  async chat(
    message: string,
    history: Array<{ role: 'user' | 'assistant'; content: string }>,
    conversationId?: string | null,
    portfolioId?: string | null,
  ) {
    const normalizedHistory = history
      .slice(-MAX_CHAT_HISTORY_MESSAGES)
      .map((item) => ({
        role: item.role,
        content: item.content.trim(),
      }))
      .filter((item) => item.content.length > 0)

    const response = await api.post('/testops-hub/chat', {
      message: message.trim(),
      conversation_id: conversationId ?? null,
      history: normalizedHistory,
      portfolio_id: portfolioId ?? null,
    })
    return response.data as {
      conversation_id: string
      content: string
      model: string
      pending_action: PendingActionPayload | null
      sources: Array<{
        snapshot_id: string
        page_title: string
        url: string
        portfolio_id: string
        relevance_score: number
      }>
      tool_calls: ToolCallRecord[]
    }
  },

  async getConversation(conversationId: string) {
    const response = await api.get(`/testops-hub/conversations/${conversationId}`)
    return response.data as {
      id: string
      title: string
      portfolio_id: string | null
      panel_state: Record<string, unknown> | null
      pending_action: PendingActionPayload | null
      created_at: string
      updated_at: string
      messages: Array<{
        id: string
        role: 'user' | 'assistant'
        content: string
        model?: string | null
        sources: Array<{
          snapshot_id: string
          page_title: string
          url: string
          portfolio_id: string
          relevance_score: number
        }>
        tool_calls?: ToolCallRecord[] | null
        created_at: string
      }>
    }
  },

  async getLatestConversation() {
    const response = await api.get('/testops-hub/conversations/latest')
    return response.data as null | {
      id: string
      title: string
      portfolio_id: string | null
      pending_action: PendingActionPayload | null
      created_at: string
      updated_at: string
      messages: Array<{
        id: string
        role: 'user' | 'assistant'
        content: string
        model?: string | null
        sources: Array<{
          snapshot_id: string
          page_title: string
          url: string
          portfolio_id: string
          relevance_score: number
        }>
        tool_calls?: ToolCallRecord[] | null
        created_at: string
      }>
    }
  },

  async listConversations(limit = 50) {
    const response = await api.get('/testops-hub/conversations', { params: { limit } })
    return response.data as Array<{
      id: string
      title: string
      portfolio_id: string | null
      updated_at: string
      message_count: number
      last_message_preview: string | null
    }>
  },

  async deleteConversation(conversationId: string) {
    await api.delete(`/testops-hub/conversations/${conversationId}`)
  },

  async renameConversation(conversationId: string, title: string) {
    return api.patch<{ id: string; title: string }>(
      `/testops-hub/conversations/${conversationId}/title`,
      { title },
    )
  },

  async submitFeedback(messageId: string, feedback: 'up' | 'down' | null) {
    return api.patch(`/testops-hub/messages/${messageId}/feedback`, { feedback })
  },

  /**
   * Resource pins — what the resolver is currently assuming the user means
   * when they say "the repo" / "our project" / "the docs space".
   */
  async listActivePins(): Promise<ActivePin[]> {
    try {
      const response = await api.get('/testops-hub/active-pins')
      return response.data as ActivePin[]
    } catch (error) {
      console.error('Failed to load active pins:', error)
      return []
    }
  },

  async clearActivePin(kind: ActivePinKind, connectionId: string): Promise<void> {
    await api.delete(`/testops-hub/active-pins/${kind}/${connectionId}`)
  },

  // Get integration status from MCP Gateway
  async getIntegrations(): Promise<Integration[]> {
    try {
      const [connections, servers] = await Promise.all([
        fetchMCPConnections(),
        fetchMCPServers(),
      ])

      return connections.map((connection) => {
        const server = servers.find((item) => item.server_key === connection.server_key)
        const isConnected = connection.status === 'active'

        return {
          name: server?.name || connection.connection_name || connection.server_key,
          status: isConnected ? 'connected' : 'disconnected',
          provider: connection.server_key,
          lastSync: connection.last_used_at ? new Date(connection.last_used_at) : undefined,
          capabilities: server?.supported_tools || [],
          connectionId: connection.id,
          errorMessage: !isConnected ? `Connection ${connection.status}` : undefined
        }
      })
    } catch (error) {
      console.error('Failed to load integrations from MCP:', error)
      // Return empty array on error
      return []
    }
  },

  // Connect integration
  async connectIntegration(provider: string, credentials: any): Promise<void> {
    await createMCPConnection({
      server_key: provider,
      connection_name: `${provider} Connection`,
      config: credentials,
    })
  },

  // Disconnect integration
  async disconnectIntegration(connectionId: string): Promise<void> {
    await deleteMCPConnection(connectionId)
  },

  // Test connection
  async testConnection(connectionId: string): Promise<boolean> {
    try {
      const response = await testMCPConnection(connectionId)
      return response.success
    } catch {
      return false
    }
  },

  /**
   * Action Items
   */
  async getActionItems(): Promise<ActionItem[]> {
    try {
      const response = await api.get('/testops-hub/action-items')
      return response.data as ActionItem[]
    } catch (error) {
      console.error('Failed to fetch action items:', error)
      return []
    }
  },

  // Update action item status.
  // TODO: Backend PATCH /testops-hub/action-items/:id not yet implemented.
  // Stub returns failure so callers handle gracefully instead of 404.
  async updateActionItem(_itemId: string, _status: string) {
    console.warn('updateActionItem: backend endpoint not yet implemented')
    return { success: false, error: 'Not implemented' }
  },

}

export default testOpsService
